import type {
  ApiContext,
  ApiNotAuthorized,
  EventNotFound,
  EventTableKind,
  EventTableKinds,
  EventTicketKind,
  EventTicketKinds,
  HostedEvent,
  HostedEventRefunds,
  HostedEvents,
  HostedEventUpdate,
  HostingOff,
  InvalidFormField,
  LocationNotFound,
  NewHostedEvent,
  NewTableKind,
  NewTicketKind,
  ShowCancelled,
  StoreNotFound,
  StoreRoleForbidden,
  TableKindNotFound,
  TableKindUpdate,
  TableNotFound,
  TableRefundRefused,
  TablesTaken,
  TicketKindNotFound,
  TicketKindUpdate,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { BackendApi } from "./api";

interface ShowCall {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  /** The shop, never a `loc_` entry id: a show is the whole shop's. */
  storeId: string;
}

export function loadHostedEvents(o: ShowCall): AsyncResult<HostedEvents, ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.list(ctx, o.storeId));
}

export function loadHostedEvent(
  o: ShowCall & { eventId: string },
): AsyncResult<HostedEvent, ApiNotAuthorized | StoreNotFound | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.get(ctx, o.storeId, o.eventId));
}

export function createHostedEvent(
  o: ShowCall & { body: NewHostedEvent },
): AsyncResult<
  HostedEvent,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | LocationNotFound | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.create(ctx, o.storeId, o.body));
}

export function updateHostedEvent(
  o: ShowCall & { eventId: string; body: HostedEventUpdate },
): AsyncResult<
  HostedEvent,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.update(ctx, o.storeId, o.eventId, o.body));
}

export function uploadHostedEventFlyer(
  o: ShowCall & { eventId: string; image: string },
): AsyncResult<
  HostedEvent,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.uploadFlyer(ctx, o.storeId, o.eventId, o.image));
}

export function publishHostedEvent(
  o: ShowCall & { eventId: string },
): AsyncResult<
  HostedEvent,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled
> {
  return o.authHandler.callApi((ctx) => o.client.events.publish(ctx, o.storeId, o.eventId));
}

export function unpublishHostedEvent(
  o: ShowCall & { eventId: string },
): AsyncResult<HostedEvent, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.unpublish(ctx, o.storeId, o.eventId));
}

export function loadTableKinds(
  o: ShowCall & { eventId: string },
): AsyncResult<EventTableKinds, ApiNotAuthorized | StoreNotFound | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.tableKinds(ctx, o.storeId, o.eventId));
}

export function createTableKind(
  o: ShowCall & { eventId: string; body: NewTableKind },
): AsyncResult<
  EventTableKind,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.createTableKind(ctx, o.storeId, o.eventId, o.body));
}

export function updateTableKind(
  o: ShowCall & { eventId: string; kindId: string; body: TableKindUpdate },
): AsyncResult<
  EventTableKind,
  | ApiNotAuthorized
  | StoreNotFound
  | StoreRoleForbidden
  | HostingOff
  | EventNotFound
  | ShowCancelled
  | TableKindNotFound
  | TablesTaken
  | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.updateTableKind(ctx, o.storeId, o.eventId, o.kindId, o.body));
}

export function deleteTableKind(
  o: ShowCall & { eventId: string; kindId: string },
): AsyncResult<
  void,
  | ApiNotAuthorized
  | StoreNotFound
  | StoreRoleForbidden
  | HostingOff
  | EventNotFound
  | ShowCancelled
  | TableKindNotFound
  | TablesTaken
> {
  return o.authHandler.callApi((ctx) => o.client.events.deleteTableKind(ctx, o.storeId, o.eventId, o.kindId));
}

export function loadTicketKinds(
  o: ShowCall & { eventId: string },
): AsyncResult<EventTicketKinds, ApiNotAuthorized | StoreNotFound | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.ticketKinds(ctx, o.storeId, o.eventId));
}

export function createTicketKind(
  o: ShowCall & { eventId: string; body: NewTicketKind },
): AsyncResult<
  EventTicketKind,
  ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.createTicketKind(ctx, o.storeId, o.eventId, o.body));
}

export function updateTicketKind(
  o: ShowCall & { eventId: string; kindId: string; body: TicketKindUpdate },
): AsyncResult<
  EventTicketKind,
  | ApiNotAuthorized
  | StoreNotFound
  | StoreRoleForbidden
  | HostingOff
  | EventNotFound
  | ShowCancelled
  | TicketKindNotFound
  | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.events.updateTicketKind(ctx, o.storeId, o.eventId, o.kindId, o.body));
}

export function deleteTicketKind(
  o: ShowCall & { eventId: string; kindId: string },
): AsyncResult<
  void,
  | ApiNotAuthorized
  | StoreNotFound
  | StoreRoleForbidden
  | HostingOff
  | EventNotFound
  | ShowCancelled
  | TicketKindNotFound
> {
  return o.authHandler.callApi((ctx) => o.client.events.deleteTicketKind(ctx, o.storeId, o.eventId, o.kindId));
}

export function cancelHostedEvent(
  o: ShowCall & { eventId: string },
): AsyncResult<HostedEventRefunds, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.cancel(ctx, o.storeId, o.eventId));
}

export function retryHostedEventRefunds(
  o: ShowCall & { eventId: string },
): AsyncResult<HostedEventRefunds, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound> {
  return o.authHandler.callApi((ctx) => o.client.events.retryRefunds(ctx, o.storeId, o.eventId));
}

export function refundTable(
  o: ShowCall & { eventId: string; tableId: string },
): AsyncResult<
  EventTableKind,
  | ApiNotAuthorized
  | StoreNotFound
  | StoreRoleForbidden
  | EventNotFound
  | ShowCancelled
  | TableNotFound
  | TableRefundRefused
> {
  return o.authHandler.callApi((ctx) => o.client.events.refundTable(ctx, o.storeId, o.eventId, o.tableId));
}
